import { useMemo } from "react";
import { Link, useLocation } from "react-router";
import { useDocumentTitle } from "../../../use-document-title";
import { useSecondaryHeader } from "../../../secondary-header-context";
import { WorkflowRunsTable } from "./runs-table";
import { WorkflowSummaryCards } from "./summary-cards";
import styles from "./index.module.css";
import { useFilteredWorkflowStatusHistory } from "../../../workflow-history-api";
import { isWorkflowHistoryWarming } from "../../../workflow-history-state";
import { useWorkflowQueryState } from "../../../workflow-query-state";
import { WorkflowHistoryChart } from "./workflow-history-chart";
import { WorkflowHistoryFilterControls } from "./workflow-history-filter-controls";

const PAGE_NAV = [
  { href: "/workflows", label: "Overview" },
  { href: "/workflows/runs", label: "Runs" },
  { href: "/workflows/history", label: "History" },
  { href: "/workflows/definitions", label: "Definitions" },
];

/**
 * `/admin/workflows` dashboard route: the top summary-cards row (active
 * runs, workflows, scheduled workflows, infra schedules — see
 * `./summary-cards.tsx`) over the full-width runs table (`./runs-table.tsx`),
 * inside this app's ambient page padding. Every section is backed by the
 * workflow BFF.
 */
export function AdminWorkflowsPage() {
  useDocumentTitle("Workflows");
  const location = useLocation();
  const { query, filters, setFilters } = useWorkflowQueryState();
  const dashboardFilters = useMemo(() => ({
    ...filters,
    status: query.status,
  }), [filters, query.status]);
  const history = useFilteredWorkflowStatusHistory(filters);
  const secondaryHeader = useMemo(() => (
    <div className={styles.secondaryHeader}>
      <nav className={styles.pageNav} aria-label="Workflow pages">
        {PAGE_NAV.map((item) => (
          <Link
            key={item.href}
            to={`${item.href}${location.search}`}
            className={`${styles.pageNavLink} ${item.href === "/workflows" ? styles.pageNavLinkActive : ""}`}
          >
            {item.label}
          </Link>
        ))}
      </nav>
      <div className={styles.filterControls}>
        <WorkflowHistoryFilterControls
          filters={dashboardFilters}
          asOf={history.data?.asOf}
          onFiltersChange={setFilters}
          statusMode="raw"
        />
      </div>
    </div>
  ), [dashboardFilters, history.data?.asOf, location.search, setFilters]);
  useSecondaryHeader(secondaryHeader);

  return (
    <div className={styles.page}>
      <WorkflowSummaryCards />
      <WorkflowHistoryChart
        points={history.data?.points ?? []}
        period={filters.period}
        onPeriodChange={(next) => {
          if (next !== "custom") setFilters({ period: next, from: "", to: "" });
        }}
        loading={history.isLoading}
        error={history.isError}
        warming={isWorkflowHistoryWarming(history.data)}
        asOf={history.data?.asOf}
      />
      <WorkflowRunsTable />
    </div>
  );
}
